/**
 * Bandeja de entrada: lista (izquierda), hilo (centro) y ficha (derecha).
 *
 * Toda la actualizacion en tiempo real se hace por id: los eventos SSE tocan
 * solo el elemento afectado, nunca recargan la lista completa.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, UIEvent } from 'react';
import type {
  ConversationDetail,
  ConversationListQuery,
  ConversationSummary,
  MessageView,
  SendMessageRequest,
  TeamMemberView,
  WhatsappAccountView,
} from '@shared/api-types.ts';
import { CONVERSATION_STATUS_LABELS } from '@domain/types.ts';
import { api, errorMessage, isAbortError, isApiError } from '../api.ts';
import type { UpdateConversationRequest } from '../api.ts';
import { formatPhone } from '../format.ts';
import type { NoticeKind } from '../hooks.ts';
import { useDebouncedValue, useRealtime, useRealtimeStatus } from '../hooks.ts';
import type { AssigneeFilter, InboxFilters, Session, StatusFilter } from '../state.ts';
import {
  CONVERSATION_PAGE_SIZE,
  DEFAULT_FILTERS,
  MESSAGE_PAGE_SIZE,
  applyMessageStatus,
  clearConversationUnread,
  conversationDisplayName,
  createOptimisticMessage,
  failOptimistic,
  matchesFilters,
  mergeMessage,
  messagePreview,
  newOptimisticId,
  patchConversationAssignee,
  replaceOptimistic,
  sortConversations,
  sortMessages,
  touchConversationPreview,
  upsertConversation,
} from '../state.ts';
import { Avatar } from './Avatar.tsx';
import { Composer } from './Composer.tsx';
import { ConversationList } from './ConversationList.tsx';
import { ConversationPanel } from './ConversationPanel.tsx';
import { Chip, EmptyState, ErrorBanner, SkeletonRows } from './Feedback.tsx';
import { Icon } from './Icon.tsx';
import { MessageThread } from './MessageThread.tsx';

type LoadStatus = 'loading' | 'ready' | 'error';

function appendPage(list: ConversationSummary[], items: ConversationSummary[]): ConversationSummary[] {
  let next = list;
  for (const item of items) next = upsertConversation(next, item);
  return next;
}

export function InboxScreen({
  session,
  conversationId,
  onOpenConversation,
  onNotice,
}: {
  session: Session;
  conversationId: string | null;
  onOpenConversation: (id: string | null) => void;
  onNotice: (kind: NoticeKind, text: string) => void;
}): ReactElement {
  // --- Filtros ---------------------------------------------------------------
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_FILTERS.status);
  const [assignee, setAssignee] = useState<AssigneeFilter>(DEFAULT_FILTERS.assignee);
  const [accountId, setAccountId] = useState<string>(DEFAULT_FILTERS.accountId);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const filters = useMemo<InboxFilters>(
    () => ({ status, assignee, accountId, q: debouncedSearch }),
    [status, assignee, accountId, debouncedSearch],
  );
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // --- Estado de la lista ----------------------------------------------------
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [listStatus, setListStatus] = useState<LoadStatus>('loading');
  const [listError, setListError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  // --- Estado del hilo y de la ficha ----------------------------------------
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageView[]>([]);
  const [messagesStatus, setMessagesStatus] = useState<LoadStatus>('ready');
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // --- Acciones --------------------------------------------------------------
  const [sending, setSending] = useState(false);
  const [patching, setPatching] = useState(false);
  const [templateRequired, setTemplateRequired] = useState<string | null>(null);
  const [typing, setTyping] = useState<'bot' | 'agent' | null>(null);

  // --- Datos auxiliares y presentacion --------------------------------------
  const [accounts, setAccounts] = useState<WhatsappAccountView[]>([]);
  const [team, setTeam] = useState<TeamMemberView[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const selectedIdRef = useRef<string | null>(conversationId);
  useEffect(() => {
    selectedIdRef.current = conversationId;
  }, [conversationId]);

  const threadRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const restoreScrollRef = useRef<number | null>(null);
  const loadingOlderRef = useRef(false);
  const olderCursorRef = useRef<string | null>(null);
  const typingTimerRef = useRef<number | null>(null);

  const listQuery = useMemo<ConversationListQuery>(
    () => ({
      status: filters.status,
      assigneeUserId: filters.assignee === 'all' ? undefined : filters.assignee,
      accountId: filters.accountId === 'all' ? undefined : filters.accountId,
      q: filters.q.trim() === '' ? undefined : filters.q.trim(),
      limit: CONVERSATION_PAGE_SIZE,
    }),
    [filters],
  );

  // Cuentas y equipo: alimentan los filtros y el selector de asignacion.
  useEffect(() => {
    const controller = new AbortController();
    void api
      .listAccounts(controller.signal)
      .then(setAccounts)
      .catch((error: unknown) => {
        if (!isAbortError(error)) onNotice('warning', 'No se pudieron cargar las cuentas de WhatsApp.');
      });
    void api
      .listTeam(controller.signal)
      .then(setTeam)
      .catch((error: unknown) => {
        if (!isAbortError(error)) onNotice('warning', 'No se pudo cargar el equipo de agentes.');
      });
    return () => controller.abort();
  }, [onNotice]);

  // Primera pagina de conversaciones (y al cambiar los filtros).
  useEffect(() => {
    const controller = new AbortController();
    setListStatus('loading');
    setListError(null);
    api
      .listConversations(listQuery, controller.signal)
      .then((page) => {
        setConversations(sortConversations(page.items));
        setNextCursor(page.nextCursor);
        setTotal(page.total);
        setListStatus('ready');
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setListError(errorMessage(error));
        setListStatus('error');
      });
    return () => controller.abort();
  }, [listQuery, reloadToken]);

  // Conversacion abierta: ficha + primera pagina de mensajes + marcar leida.
  useEffect(() => {
    if (!conversationId) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      setMessages([]);
      setMessagesError(null);
      setMessagesStatus('ready');
      setOlderCursor(null);
      olderCursorRef.current = null;
      setTyping(null);
      return;
    }

    const controller = new AbortController();
    nearBottomRef.current = true;
    setDetailLoading(true);
    setDetailError(null);
    setMessages([]);
    setMessagesError(null);
    setMessagesStatus('loading');
    setOlderCursor(null);
    olderCursorRef.current = null;
    setTyping(null);
    setTemplateRequired(null);

    api
      .getConversation(conversationId, controller.signal)
      .then((result) => {
        setDetail(result);
        setConversations((prev) => clearConversationUnread(prev, result.id));
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setDetailError(errorMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });

    api
      .listMessages(conversationId, { limit: MESSAGE_PAGE_SIZE }, controller.signal)
      .then((page) => {
        setMessages(sortMessages(page.items));
        setOlderCursor(page.nextCursor);
        olderCursorRef.current = page.nextCursor;
        setMessagesStatus('ready');
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setMessagesError(errorMessage(error));
        setMessagesStatus('error');
      });

    api.markRead(conversationId, controller.signal).catch(() => {
      // Marcar como leida es un extra: si falla, no se molesta al agente.
    });

    return () => controller.abort();
  }, [conversationId, reloadToken]);

  // Reloj local: la ventana de 24 h puede cerrarse mientras el panel esta abierto.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  // Al recuperar el tiempo real se reconcilia el estado por si se perdio algo.
  const sseStatus = useRealtimeStatus();
  const previousSseRef = useRef(sseStatus);
  useEffect(() => {
    const previous = previousSseRef.current;
    previousSseRef.current = sseStatus;
    if (sseStatus === 'open' && (previous === 'offline' || previous === 'reconnecting')) {
      setReloadToken((token) => token + 1);
    }
  }, [sseStatus]);

  // --- Tiempo real -----------------------------------------------------------

  /**
   * `EventSource` no puede enviar la cabecera `X-Tenant-Id`, asi que la API
   * resuelve la organizacion del canal por el token. Cuando la persona
   * pertenece a varias organizaciones se descartan los eventos cuya cuenta no
   * sea de la organizacion activa (se conocen por `/api/accounts`) para no
   * mezclar conversaciones de otra organizacion en la bandeja.
   */
  const acceptsTenantEvent = (conversation: ConversationSummary): boolean => {
    if (session.memberships.length <= 1 || accounts.length === 0) return true;
    return accounts.some((account) => account.id === conversation.account.id);
  };

  useRealtime((event) => {
    const activeId = selectedIdRef.current;

    switch (event.type) {
      case 'message.created': {
        const { conversationId: eventConversationId, conversation, message } = event;
        if (!acceptsTenantEvent(conversation)) break;
        setConversations((prev) => {
          const exists = prev.some((item) => item.id === conversation.id);
          if (!exists && !matchesFilters(conversation, filtersRef.current, session.user.id)) return prev;
          const summary =
            eventConversationId === activeId && message.direction === 'inbound'
              ? { ...conversation, unreadCount: 0 }
              : conversation;
          return upsertConversation(prev, summary);
        });
        if (eventConversationId === activeId) {
          setMessages((prev) => mergeMessage(prev, message));
          if (message.direction === 'inbound') {
            api.markRead(eventConversationId).catch(() => {
              // Sin consecuencia para el agente si falla.
            });
          }
        }
        break;
      }

      case 'message.status': {
        const {
          conversationId: eventConversationId,
          messageId,
          status: messageStatus,
          error,
          errorCode,
          errorTitle,
        } = event;
        if (eventConversationId === activeId) {
          setMessages((prev) =>
            applyMessageStatus(prev, messageId, messageStatus, error ?? null, errorCode ?? null, errorTitle ?? null),
          );
        }
        break;
      }

      case 'conversation.updated': {
        const { conversation } = event;
        if (!acceptsTenantEvent(conversation)) break;
        setConversations((prev) => {
          const exists = prev.some((item) => item.id === conversation.id);
          if (!exists && !matchesFilters(conversation, filtersRef.current, session.user.id)) return prev;
          return upsertConversation(prev, conversation);
        });
        setDetail((prev) => (prev && prev.id === conversation.id ? { ...prev, ...conversation } : prev));
        break;
      }

      case 'conversation.assigned': {
        const { conversationId: eventConversationId, assignee: newAssignee } = event;
        setConversations((prev) => patchConversationAssignee(prev, eventConversationId, newAssignee));
        setDetail((prev) =>
          prev && prev.id === eventConversationId ? { ...prev, assignee: newAssignee } : prev,
        );
        break;
      }

      case 'typing': {
        if (event.conversationId !== activeId) break;
        setTyping(event.who);
        if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
        typingTimerRef.current = window.setTimeout(() => setTyping(null), 5000);
        break;
      }
    }
  });

  useEffect(
    () => () => {
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
    },
    [],
  );

  // --- Acciones --------------------------------------------------------------
  const loadMore = useCallback((): void => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    api
      .listConversations({ ...listQuery, cursor: nextCursor })
      .then((page) => {
        setConversations((prev) => appendPage(prev, page.items));
        setNextCursor(page.nextCursor);
      })
      .catch((error: unknown) => onNotice('error', errorMessage(error)))
      .finally(() => setLoadingMore(false));
  }, [listQuery, nextCursor, loadingMore, onNotice]);

  const loadOlder = useCallback((): void => {
    const id = selectedIdRef.current;
    const cursor = olderCursorRef.current;
    if (!id || !cursor || loadingOlderRef.current) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const container = threadRef.current;
    const previousHeight = container ? container.scrollHeight : null;

    api
      .listMessages(id, { limit: MESSAGE_PAGE_SIZE, cursor })
      .then((page) => {
        setMessages((prev) => {
          let next = prev;
          for (const message of page.items) next = mergeMessage(next, message);
          return next;
        });
        setOlderCursor(page.nextCursor);
        olderCursorRef.current = page.nextCursor;
        if (previousHeight !== null) restoreScrollRef.current = previousHeight;
      })
      .catch((error: unknown) => {
        if (!isAbortError(error)) onNotice('error', errorMessage(error));
      })
      .finally(() => {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      });
  }, [onNotice]);

  const handleThreadScroll = useCallback(
    (event: UIEvent<HTMLDivElement>): void => {
      const element = event.currentTarget;
      const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
      nearBottomRef.current = distance < 140;
      if (element.scrollTop < 90) loadOlder();
    },
    [loadOlder],
  );

  const handlePatch = useCallback(
    (patch: UpdateConversationRequest): void => {
      const id = selectedIdRef.current;
      if (!id) return;
      setPatching(true);
      api
        .updateConversation(id, patch)
        .then((updated) => {
          setDetail(updated);
          setConversations((prev) => {
            const rest = prev.filter((item) => item.id !== updated.id);
            return matchesFilters(updated, filtersRef.current, session.user.id)
              ? upsertConversation(rest, updated)
              : rest;
          });
          if (patch.status) {
            onNotice('success', `Estado actualizado: ${CONVERSATION_STATUS_LABELS[patch.status]}.`);
          }
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setPatching(false));
    },
    [onNotice, session.user.id],
  );

  const handleSend = useCallback(
    async (request: SendMessageRequest): Promise<boolean> => {
      const id = selectedIdRef.current;
      if (!id) return false;

      const optimisticId = newOptimisticId();
      setMessages((prev) =>
        mergeMessage(
          prev,
          createOptimisticMessage(optimisticId, request, {
            id: session.user.id,
            name: session.user.name,
          }),
        ),
      );
      setSending(true);

      try {
        const response = await api.sendMessage(id, request);
        setMessages((prev) => replaceOptimistic(prev, optimisticId, response.message));
        setConversations((prev) =>
          touchConversationPreview(prev, id, messagePreview(response.message), response.message.createdAt),
        );
        setTemplateRequired(null);
        return true;
      } catch (error: unknown) {
        const message = errorMessage(error);
        // El `code` de `ApiError` es un codigo propio del backend (por ejemplo
        // `window_closed`), no un codigo de Meta: no se muestra como si lo
        // fuera. El texto de `message` ya viene traducido y es autosuficiente.
        setMessages((prev) => failOptimistic(prev, optimisticId, message, null));
        if (isApiError(error) && error.requiresTemplate) {
          setTemplateRequired(message);
          onNotice('warning', message);
        } else {
          onNotice('error', message);
        }
        return false;
      } finally {
        setSending(false);
      }
    },
    [onNotice, session.user.id, session.user.name],
  );

  const handleSelect = useCallback(
    (id: string): void => {
      setPanelOpen(false);
      onOpenConversation(id);
    },
    [onOpenConversation],
  );

  // Al cambiar de conversacion el hilo se coloca abajo del todo.
  useLayoutEffect(() => {
    const container = threadRef.current;
    if (!container) return;
    const restore = restoreScrollRef.current;
    if (restore !== null) {
      restoreScrollRef.current = null;
      container.scrollTop = container.scrollHeight - restore;
      return;
    }
    if (nearBottomRef.current) container.scrollTop = container.scrollHeight;
  }, [messages]);

  const windowState = useMemo(() => {
    if (!detail) return null;
    const expiresAt = detail.window.expiresAt ? Date.parse(detail.window.expiresAt) : null;
    const isOpen = detail.window.isOpen && (expiresAt === null || expiresAt > now);
    return { isOpen, label: isOpen ? detail.window.label : 'cerrada' };
  }, [detail, now]);

  const selectedSummary = useMemo(
    () => conversations.find((item) => item.id === conversationId) ?? null,
    [conversations, conversationId],
  );
  const headerName = detail
    ? conversationDisplayName(detail)
    : selectedSummary
      ? conversationDisplayName(selectedSummary)
      : '';
  const headerPhone = detail
    ? formatPhone(detail.contact.waId)
    : selectedSummary
      ? formatPhone(selectedSummary.contact.waId)
      : '';
  const unreadTotal = useMemo(
    () => conversations.reduce((sum, item) => sum + item.unreadCount, 0),
    [conversations],
  );

  const hasOlder = olderCursor !== null;

  return (
    <div
      className={[
        'inbox',
        panelOpen ? 'is-panel-open' : '',
        conversationId ? 'is-thread-active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ConversationList
        conversations={conversations}
        loading={listStatus === 'loading'}
        loadingMore={loadingMore}
        hasMore={nextCursor !== null}
        error={listStatus === 'error' ? listError : null}
        selectedId={conversationId}
        search={search}
        status={status}
        assignee={assignee}
        accountId={accountId}
        accounts={accounts}
        unreadTotal={unreadTotal}
        total={total}
        currentUserId={session.user.id}
        onSearchChange={setSearch}
        onStatusChange={setStatus}
        onAssigneeChange={setAssignee}
        onAccountChange={setAccountId}
        onSelect={handleSelect}
        onLoadMore={loadMore}
        onRetry={() => setReloadToken((token) => token + 1)}
      />

      <section className="center" aria-label="Hilo de la conversacion">
        {conversationId ? (
          <>
            <header className="center__header">
              <button
                type="button"
                className="button button--ghost button--sm center__back"
                onClick={() => onOpenConversation(null)}
                aria-label="Volver a la lista"
              >
                <Icon name="back" />
              </button>

              <Avatar name={headerName} seed={detail?.contact.id ?? conversationId} size="sm" />
              <div className="center__title">
                <p className="center__name">{headerName || 'Cargando…'}</p>
                <p className="center__phone">{headerPhone}</p>
              </div>

              <div className="center__chips">
                {detail ? (
                  <>
                    <Chip tone={detail.status === 'resolved' ? 'success' : 'info'}>
                      {CONVERSATION_STATUS_LABELS[detail.status]}
                    </Chip>
                    <Chip tone="neutral" title="Modo del asistente de IA">
                      {detail.botMode === 'auto'
                        ? 'IA activa'
                        : detail.botMode === 'paused'
                          ? 'Atiende una persona'
                          : 'IA desactivada'}
                    </Chip>
                  </>
                ) : null}
              </div>

              <button
                type="button"
                className="button button--ghost button--sm center__panel"
                onClick={() => setPanelOpen((open) => !open)}
                aria-expanded={panelOpen}
              >
                <Icon name="panel" />
                Ficha
              </button>
            </header>

            {detailError ? <ErrorBanner message={detailError} onRetry={() => setReloadToken((t) => t + 1)} /> : null}

            <MessageThread
              messages={messages}
              contactName={headerName}
              loading={messagesStatus === 'loading'}
              error={messagesStatus === 'error' ? messagesError : null}
              onRetry={() => setReloadToken((token) => token + 1)}
              loadingOlder={loadingOlder}
              hasOlder={hasOlder}
              onLoadOlder={loadOlder}
              containerRef={threadRef}
              onScroll={handleThreadScroll}
              typing={typing}
            />

            {detail && windowState ? (
              <Composer
                accountId={detail.account.id}
                windowOpen={windowState.isOpen}
                windowLabel={windowState.label}
                sending={sending}
                templateRequired={templateRequired}
                onSend={handleSend}
                onDismissTemplateRequired={() => setTemplateRequired(null)}
              />
            ) : (
              <div className="composer composer--placeholder">
                <SkeletonRows rows={2} />
              </div>
            )}
          </>
        ) : (
          <div className="center__empty">
            <EmptyState
              icon="inbox"
              title="Selecciona una conversacion"
              description="Elige un contacto de la lista para ver el hilo de mensajes y responder. Los mensajes nuevos llegan en tiempo real."
            />
          </div>
        )}
      </section>

      <ConversationPanel
        detail={detail}
        loading={detailLoading}
        error={detailError}
        team={team}
        currentUserId={session.user.id}
        patching={patching}
        onRetry={() => setReloadToken((token) => token + 1)}
        onPatch={handlePatch}
        onClose={() => setPanelOpen(false)}
      />
    </div>
  );
}
